import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

/** @typedef {import("./backend.types.d.ts").SimplifiedEnvelope} SimplifiedEnvelope */
/** @typedef {import("./backend.types.d.ts").SimplifiedRowSeries} SimplifiedRowSeries */
/** @typedef {import("./backend.types.d.ts").SimplifiedPoint} SimplifiedPoint */

function byISOAsc(a, b) {
  return a === b ? 0 : a < b ? -1 : 1; // works for YYYY-MM-DD
}

/** return YYYY-MM-DD shifted by `days` */
function shiftISO(iso, days) {
  return dayjs(iso).add(days, "day").format(BACKEND_DATE_FORMAT);
}

/**
 * Ensure a point for each date (default: { isHarvested:false, avgLeafCm:null }).
 * @param {string[]} dates
 * @param {SimplifiedPoint[]} points
 * @returns {SimplifiedPoint[]}
 */
function densifyPoints(dates, points) {
  const map = new Map((points || []).map((p) => [p.date, p]));
  return dates.map((d) => {
    const p = map.get(d);
    return p
      ? {
          date: p.date,
          isHarvested: !!p.isHarvested,
          avgLeafCm:
            p.avgLeafCm === null || p.avgLeafCm === undefined
              ? null
              : Number.isFinite(Number(p.avgLeafCm))
                ? Number(p.avgLeafCm)
                : null,
        }
      : { date: d, isHarvested: false, avgLeafCm: null };
  });
}

/**
 * Pick exactly two anchor points (< selectedISO) for forecasting.
 * Rules:
 *   - Prefer anchors inside [selectedISO - forecastDays .. selectedISO).
 *   - If only one inside, add the closest older one from before that window.
 *   - If none inside, take the last two overall from < selectedISO (if available).
 * Returns ascending by date (older, newer). Can return 0, 1 or 2 items.
 *
 * @param {SimplifiedPoint[]} dense
 * @param {string} selectedISO
 * @param {number} forecastDays
 * @returns {SimplifiedPoint[]}
 */
function pickAnchors(dense, selectedISO, forecastDays) {
  const windowStart = shiftISO(selectedISO, -forecastDays);

  const pastActuals = dense
    .filter((p) => p.date < selectedISO && p.avgLeafCm != null)
    .sort((a, b) => byISOAsc(a.date, b.date));

  if (pastActuals.length === 0) return [];

  const inWin = pastActuals.filter((p) => p.date >= windowStart);
  if (inWin.length >= 2) return inWin.slice(-2);
  if (inWin.length === 1) {
    const lone = inWin[0];
    const older = pastActuals.filter((p) => p.date < windowStart);
    if (older.length > 0) return [older[older.length - 1], lone];
    return [lone];
  }
  // none in window -> try last two overall (closest to selected)
  return pastActuals.slice(-2);
}

/**
 * Apply trimming/splitting with a recency gate for UNHARVESTED rows:
 * - Remove harvested rows if harvest < startISO (not shown in window).
 * - For unharvested rows, require latest actual point (<= selectedISO)
 *   to be >= selectedISO - forecastDays; otherwise drop the row.
 * - For unharvested rows, require at least TWO non-zero past actuals (<= selectedISO).
 */
function filterAndSplit(
  series,
  dates,
  selectedISO,
  startISO,
  endISO,
  forecastDays,
) {
  const harvestedData = [];
  const unharvestedData = [];

  const windowDates = [...dates].sort(byISOAsc);
  const minAllowedObsISO = shiftISO(selectedISO, -forecastDays);

  for (const s of series || []) {
    const dense = densifyPoints(windowDates, s.points || []);

    // earliest harvest within the visible window (if any)
    let earliestHarvest = null;
    for (const p of dense) {
      if (p.isHarvested) {
        earliestHarvest = p.date;
        break;
      }
    }

    // Case 1: harvested before/at selected date.
    // If harvested < startISO -> drop entirely (not shown).
    if (earliestHarvest && earliestHarvest <= selectedISO) {
      if (earliestHarvest < startISO) continue;

      const kept = dense.find((p) => p.date === earliestHarvest);
      if (kept) {
        harvestedData.push(
          /** @type {SimplifiedRowSeries} */ ({
            bayId: s.bayId,
            rowLabel: s.rowLabel,
            rowNumber: s.rowNumber,
            variety: s.variety,
            points: [{ date: kept.date, isHarvested: true, avgLeafCm: null }],
          }),
        );
      }
      continue; // nothing more to do for this row
    }

    // Case 2: not harvested before selected -> enforce recency for UNHARVESTED
    // Find latest actual (<= selectedISO); if too old, skip this row entirely.
    const pastActuals = dense
      .filter((p) => p.date <= selectedISO && p.avgLeafCm != null)
      .sort((a, b) => byISOAsc(a.date, b.date));
    const latestActual = pastActuals.length
      ? pastActuals[pastActuals.length - 1].date
      : null;

    if (!latestActual || latestActual < minAllowedObsISO) {
      continue; // Too old to project into/after selected date
    }
    const nonZeroPastActualsCount = pastActuals.filter(
      (p) => Number(p.avgLeafCm) > 0,
    ).length;
    if (nonZeroPastActualsCount < 2) {
      continue; // Not enough signal to forecast
    }

    // Build anchors + future placeholders until a harvest (if any at/after selected)
    const anchors = pickAnchors(dense, selectedISO, forecastDays);

    // Future placeholders until harvest (if any at/after selected)
    const future = [];
    let harvestInWindow = false;
    for (const p of dense) {
      if (p.date < selectedISO) continue;

      if (p.isHarvested) {
        future.push({ date: p.date, isHarvested: true, avgLeafCm: null });
        harvestInWindow = true;
        break; // stop after the harvest day
      } else {
        future.push({
          date: p.date,
          isHarvested: false,
          // keep selected day's actual if present; future days stay null
          avgLeafCm:
            p.date === selectedISO && p.avgLeafCm != null
              ? Number(p.avgLeafCm)
              : null,
        });
      }
    }

    // Assemble output points: only the anchors (no other past nulls) + future placeholders
    const pastTrimmed = anchors.map((a) => ({
      date: a.date,
      isHarvested: false,
      avgLeafCm: a.avgLeafCm, // actual values kept
    }));

    const points = [...pastTrimmed, ...future].sort((a, b) =>
      byISOAsc(a.date, b.date),
    );
    if (!points.length) continue;

    const trimmedSeries = /** @type {SimplifiedRowSeries} */ ({
      bayId: s.bayId,
      rowLabel: s.rowLabel,
      rowNumber: s.rowNumber,
      variety: s.variety,
      points,
    });

    if (harvestInWindow) {
      harvestedData.push(trimmedSeries);
    } else {
      unharvestedData.push(trimmedSeries);
    }
  }

  return { harvestedData, unharvestedData };
}

export const harvestDataFilter = {
  /**
   * @param {{
   *   selectedDate: string,
   *   backendData: SimplifiedEnvelope,
   *   startISO: string,
   *   endISO: string,
   *   forecastDays: number
   *   enableTimeMachineForPastDates?: boolean
   * }} args
   * @returns {{ harvestedData: SimplifiedRowSeries[], unharvestedData: SimplifiedRowSeries[] }}
   */
  getFilteredData({
    selectedDate,
    backendData,
    startISO,
    endISO,
    forecastDays,
    enableTimeMachineForPastDates,
  }) {
    if (!backendData || !Array.isArray(backendData.dates)) {
      return { harvestedData: [], unharvestedData: [] };
    }
    const dates = backendData.dates;
    const series = Array.isArray(backendData.series) ? backendData.series : [];

    const todayISO = dayjs().format(BACKEND_DATE_FORMAT);

    // Time Machine OFF behavior:
    // - Use an "as-of" cutoff that is independent of selectedDate:
    //   effectiveSelectedISO = min(todayISO, endISO)
    // - If the whole window is in the past (endISO < todayISO), return ONLY harvested
    if (!enableTimeMachineForPastDates) {
      const endIsBeforeToday = dayjs(
        endISO,
        BACKEND_DATE_FORMAT,
        true,
      ).isBefore(dayjs(todayISO, BACKEND_DATE_FORMAT, true), "day");

      const effectiveSelectedISO = endIsBeforeToday ? endISO : todayISO;

      const res = filterAndSplit(
        series,
        dates,
        effectiveSelectedISO,
        startISO,
        endISO,
        forecastDays,
      );

      if (endIsBeforeToday) {
        return { harvestedData: res.harvestedData, unharvestedData: [] };
      }
      return res;
    }

    // Time Machine ON
    return filterAndSplit(
      series,
      dates,
      selectedDate,
      startISO,
      endISO,
      forecastDays,
    );
  },
};
